import React from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import {
  SmartToy as AgentIcon,
  Person as UserIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  fileNames?: string[];
}

const markdownStyles = {
  '& p': { mt: 0, mb: 1, '&:last-child': { mb: 0 } },
  '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.5 },
  '& h1': { fontSize: '1.25rem' },
  '& h2': { fontSize: '1.1rem' },
  '& h3': { fontSize: '1rem' },
  '& ul, & ol': { mt: 0, mb: 1, pl: 2.5 },
  '& li': { mb: 0.25 },
  '& code': {
    px: 0.5,
    py: 0.25,
    borderRadius: 0.5,
    bgcolor: 'rgba(0,0,0,0.06)',
    fontSize: '0.85em',
    fontFamily: 'monospace',
  },
  '& pre': {
    p: 1.5,
    borderRadius: 1,
    bgcolor: 'rgba(0,0,0,0.06)',
    overflow: 'auto',
    '& code': { px: 0, py: 0, bgcolor: 'transparent' },
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    mb: 1,
    '& th, & td': {
      border: '1px solid',
      borderColor: 'divider',
      px: 1,
      py: 0.5,
      fontSize: '0.85rem',
    },
    '& th': { bgcolor: 'rgba(0,0,0,0.04)', fontWeight: 600 },
  },
  '& blockquote': {
    borderLeft: 3,
    borderColor: 'primary.main',
    pl: 1.5,
    ml: 0,
    color: 'text.secondary',
  },
  '& a': { color: 'primary.main' },
  '& hr': { my: 1, borderColor: 'divider' },
  wordBreak: 'break-word',
  fontSize: '0.875rem',
} as const;

const MessageBubble: React.FC<{ message: ChatMessage; isStreaming?: boolean }> = ({ message, isStreaming: streaming }) => {
  const isUser = message.role === 'user';

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      gap: 1,
      alignItems: 'flex-start',
    }}>
      {!isUser && (
        <AgentIcon sx={{ color: 'primary.main', mt: 0.5, fontSize: 28 }} />
      )}
      <Paper
        elevation={1}
        sx={{
          p: 1.5,
          maxWidth: '75%',
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2,
        }}
      >
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {message.content}
          </Typography>
        ) : (
          <Box sx={markdownStyles}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => {
                  const url = href && !href.startsWith('http') && !href.startsWith('mailto:')
                    ? `https://${href}`
                    : href;
                  return (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
            {streaming && !message.content && (
              <CircularProgress size={14} sx={{ ml: 1 }} />
            )}
          </Box>
        )}
        {message.fileNames && message.fileNames.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            {message.fileNames.map((name, idx) => (
              <Typography key={idx} variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                📎 {name}
              </Typography>
            ))}
          </Box>
        )}
      </Paper>
      {isUser && (
        <UserIcon sx={{ color: 'text.secondary', mt: 0.5, fontSize: 28 }} />
      )}
    </Box>
  );
};

export default MessageBubble;
